import { Injectable } from '@angular/core';

import { Matter } from '../../models/matter';

import { HttpClientService } from '../common/http-client.service';

@Injectable()
export class MatterService {
  /**
   * URL for matters controller
   * @type {String}
   */
  private mattersURL = 'matters';

  /**
   * URL for matter types controller
   * @type {String}
   */
  private matterTypesURL = 'matter_types';

  /**
   * Constructor
   * @param {HttpClientService} private httpClient Service for making HTTP requests with different methods
   */
  constructor(private httpClient: HttpClientService) {}

  /**
   * Add tag to matter
   * @param  {number}       matter_id Id of matter
   * @param  {string}       tag       Tag to add
   * @return {Promise<any>}           Server response with request status
   */
  addTag(matter_id: number, tag: string): Promise<any> {
    return this.httpClient.put(`${this.mattersURL}/${matter_id}.json`, { add_tag: true, tag: tag });
  }

  /**
   * Archive matter
   * @param  {number}       matter_id Id of matter
   * @param  {any}          params    Params for the archive request with options for archiving
   * @return {Promise<any>}           Server response with request status
   */
  archiveMatter(matter_id: number, params: any): Promise<any> {
    return this.httpClient.patch(`${this.mattersURL}/${matter_id}/archive_matter.json`, params);
  }

  /**
   * Get bulk edit form select field lists and default options
   * @return {Promise<any>} Response with bulk edit options
   */
  bulkEditOptions(): Promise<any> {
    return this.httpClient.get(`${this.mattersURL}.json`, { bulk_edit_options: true });
  }

  /**
   * Clone matter
   * @param  {any}          matterClone Clone options
   * @param  {number}       matter_id   Id of matter
   * @return {Promise<any>}             Server response with request status
   */
  clone(matterClone: any, matter_id: number): Promise<any> {
    matterClone.matter_clone = Object.assign({}, matterClone);
    matterClone.parent_id = matterClone.parent_matter_id;
    return this.httpClient.post(`${this.mattersURL}/${matter_id}/clone.json`, matterClone);
  }

  /**
   * Donation report of matter
   * @param  {number}       matterId Id of matter
   * @return {Promise<any>}          Server response from donation_request action
   */
  donationReport(matterId: number): Promise<any> {
    return this.httpClient.post(`${this.mattersURL}/${matterId}/donation_report.json`, {});
  }

  /**
   * Matter sidebar events search options
   * @param  {number}       matterId Id of matter
   * @return {Promise<any>}          Server response with search options
   */
  eventsSearchOptions(matterId: number): Promise<any> {
    return this.httpClient.get(`${this.mattersURL}/${matterId}.json`, { events_search_options: true });
  }

  /**
   * Form options for matter edit
   * @param  {number}       matter_id Id of matter
   * @return {Promise<any>}           Server response with form options
   */
  formOptions(matter_id: number): Promise<any> {
    return this.httpClient.get(`${this.mattersURL}/${matter_id}.json`, { form_options: true });
  }

  /**
   * Get billing summary for the matter
   * @param  {any}          matter_id Id of matter
   * @return {Promise<any>}           Server response with billing summary
   */
  getBillingSummary(matter_id: any): Promise<any> {
    return this.httpClient.get(`${this.mattersURL}/${matter_id}.json`, { get_billing_summary: true });
  }

  /**
   * Get matter for edit
   * @param  {number}       matter_id Id of matter
   * @return {Promise<any>}           Server response with matter for edit
   */
  getEdit(matter_id: number): Promise<any> {
    return this.httpClient.get(`${this.mattersURL}/${matter_id}/edit.json`);
  }

  /**
   * Get events for matter
   * @param  {any}          matter_id Id of matter
   * @param  {any       =         {}}        params Search params
   * @return {Promise<any>}           Server response with events
   */
  getEvents(matter_id: any, params: any = {}): Promise<any> {
    params.get_events = true;
    return this.httpClient.get(`${this.mattersURL}/${matter_id}.json`, params);
  }

  /**
   * Get exhibits for matter
   * @param  {any}          matter_id Id of matter
   * @return {Promise<any>}           Server response with exhibits
   */
  getExhibits(matter_id: any): Promise<any> {
    return this.httpClient.get(`${this.mattersURL}/${matter_id}.json`, { get_exhibits: true });
  }

  /**
   * Get involvements of matter for select field
   * @param  {[type]}       matter_id Id of matter
   * @return {Promise<any>}           Server response with involvements for select
   */
  getInvolvementsForSelect(matter_id: any): Promise<any> {
    return this.httpClient.get(this.mattersURL + '/' + matter_id + '/involvements.json', { for_select: true }).catch(function () {});
  }

  /**
   * Get associations for matter
   * @param  {any}          matter_id Id of matter
   * @return {Promise<any>}           Server response with associations
   */
  getMatterAssociations(matter_id: any): Promise<any> {
    return this.httpClient.get(`${this.mattersURL}/${matter_id}.json`, { get_associations: true });
  }

  /**
   * Get options for matter clone
   * @return {Promise<any>} Server response with options for matter clonse
   */
  getMatterCloneOptions(): Promise<any> {
    return this.httpClient.get(`${this.mattersURL}.json`, { matter_clone_options: true });
  }

  /**
   * Get matter info
   * @param  {any}          matter_id Id of matter
   * @return {Promise<any>}           Server response with matter info
   */
  getMatterInfo(matter_id: any): Promise<any> {
    return this.httpClient.get(`${this.mattersURL}/${matter_id}.json`, { get_info: true });
  }

  /**
   * Get matter status
   * @param  {any}          matter_id Id of matter
   * @return {Promise<any>}           Server response with matter status
   */
  getMatterStatusInfo(matter_id: any): Promise<any> {
    return this.httpClient.get(`${this.mattersURL}/${matter_id}.json`, { get_status_info: true });
  }

  getPayloadRetainerRequests(matter_id: any): Promise<any> {
    return this.httpClient.get(`${this.mattersURL}/${matter_id}/payload_retainer_requests.json`);
  }

  /**
   * Get matter types for intranet
   * @param  {number    = undefined}   intranet_id Id of intranet
   * @return {Promise<any>}   Server response with matter types
   */
  getMatterTypes(intranet_id: number | undefined = undefined): Promise<any> {
    return this.httpClient.get(`${this.matterTypesURL}.json`, { intranet_id: intranet_id, only_select: true });
  }

  /**
   * Rate types for given matter type
   * @param  {number}       matter_type_id Id of matter type
   * @return {Promise<any>}                Server response with rate types
   */
  getRateTypes(matter_type_id: number): Promise<any> {
    return this.httpClient.get(`${this.mattersURL}.json`, { matter_type_id: matter_type_id, from_js: true });
  }

  /**
   * Get tags of given matter
   * @param  {number}       matter_id Id of matter
   * @return {Promise<any>}           Server response with tags
   */
  getTags(matter_id: number): Promise<any> {
    return this.httpClient.get(`${this.mattersURL}/${matter_id}.json`, { get_tags: true });
  }

  /**
   * Re index solr for given parts of the matter
   * @param  {number}        id    Id of matter
   * @param  {Array<string>} types Sections of the matter to re index such as involvements, matter histories, etc.
   * @return {Promise<any>}        Server response with re index result
   */
  reindexSolr(id: number, types: Array<string>): Promise<any> {
    return this.httpClient.post(`${this.mattersURL}/${id}/reindex_solr.json`, { types: types });
  }

  /**
   * Reindex documents from actual files on disk - admin only
   * Scans matter folder, creates missing document records, and submits to Solr for content extraction
   * @param  {number}       id Id of matter
   * @return {Promise<any>}    Server response with reindex results
   */
  reindexDocumentsPlus(id: number): Promise<any> {
    return this.httpClient.post(`${this.mattersURL}/${id}/reindex_documents_plus.json`, {});
  }

  requestPayloadRetainer(matter_id: any, retainerRequest: any): Promise<any> {
    return this.httpClient.post(`${this.mattersURL}/${matter_id}/payload_retainer_requests.json`, {
      payload_retainer_request: retainerRequest,
    });
  }

  /**
   * Clean test data for matter 697 (file 10711) - admin only
   * Deletes oldest 90% of histories, documents, and accounting data
   * @param  {number}       matterId Id of matter
   * @return {Promise<any>}          Server response with cleanup results
   */
  cleanTestData(matterId: number): Promise<any> {
    return this.httpClient.post(`${this.mattersURL}/${matterId}/clean_test_data.json`, {});
  }

  /**
   * Remove tag from matter
   * @param  {number}       matter_id Id of matter
   * @param  {string}       tag       Tag to remove
   * @return {Promise<any>}           Server response with result
   */
  removeTag(matter_id: number, tag: string): Promise<any> {
    return this.httpClient.put(`${this.mattersURL}/${matter_id}.json`, { remove_tag: true, tag: tag });
  }

  /**
   * Search matters with given params
   * @param  {any}          params Search params
   * @return {Promise<any>}        Server response with matters
   */
  index(params: any = {}): Promise<any> {
    return this.httpClient.get(`${this.mattersURL}.json`, params);
  }

  /**
   * Search options for matter inde page sidebar
   * @return {Promise<any>} Server response with search options
   */
  searchOptions(): Promise<any> {
    return this.httpClient.get(`${this.mattersURL}.json`, { search_options: true });
  }

  /**
   * Update matter
   * @param  {Matter}       matter Matter to update
   * @return {Promise<any>}        Server response with result
   */
  updateMatter(matter: Matter): Promise<any> {
    return this.httpClient.put(`${this.mattersURL}/${matter.id}.json`, { angular: 1, matter: matter });
  }
}
